/**
 * @file CharacterSlideshow.js
 * @description Auto-maintained cleanup: comments & minor a11y/robustness. No UI/feature changes.
 */
import React, { useState } from 'react';
import './CharacterSlideshow.css';
import CharacterTile from './CharacterTile';

const CharacterSlideshow = ({ characters, setIsOverlayVisible, onSaveCharacter, onRegenerateCharacterFull, characterIsRegenerating, imageModels, projectId, onValidateContent }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(3); // Default to 3 for "perfect" start
  const containerRef = React.useRef(null);

  // Dynamic sizing based on container width
  React.useEffect(() => {
    if (!containerRef.current) return;

    let debounceTimer;
    const observer = new ResizeObserver((entries) => {
      // Clear previous timer to debounce rapid changes (e.g. during zoom interaction)
      if (debounceTimer) clearTimeout(debounceTimer);

      debounceTimer = setTimeout(() => {
        if (!containerRef.current) return;

        // Measure live clientWidth (content + padding) to ensure we capture the final settled state
        const clientWidth = containerRef.current.clientWidth;

        // Explicitly subtract the side padding (50px + 50px = 100px) defined in CSS
        // to get the true available width for cards
        const availableWidth = clientWidth - 100;

        // Heuristic: Min width per card 615px.
        // - Optimized "Goldilocks" value:
        // - Prevents truncation (better than 600px).
        // - More responsive than 640px/620px to allow adding cards when zooming out.
        const MIN_CARD_WIDTH = 615;
        const GAP = 70;

        const count = Math.floor((availableWidth + GAP) / (MIN_CARD_WIDTH + GAP));
        // Clamp between 1 and 4 to prevent overcrowding on wide screens
        const newItemsPerPage = Math.min(4, Math.max(1, count));

        setItemsPerPage(newItemsPerPage);
      }, 100); // 100ms debounce allows CSS transform/width updates to settle
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
      if (debounceTimer) clearTimeout(debounceTimer);
    };
  }, []);

  // Ensure we don't end up with empty slots if the window expands
  // e.g., if we are at the end showing 2 items, and suddenly we can show 3,
  // we should shift back one spot to show 3 full items.
  React.useEffect(() => {
    const maxIndex = Math.max(0, characters.length - itemsPerPage);
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [itemsPerPage, characters.length, currentIndex]);

  const goToPrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const goToNext = () => {
    if (currentIndex < characters.length - itemsPerPage) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const visibleCharacters = characters.slice(currentIndex, currentIndex + itemsPerPage);

  return (
    <div className="character-slideshow-container" ref={containerRef}>
      <button className="prev" onClick={goToPrevious} disabled={currentIndex === 0}>❮</button>
      <div className="character-slideshow" style={{ flex: 1, minWidth: 0 }}>
        {visibleCharacters.map((character, index) => (
          <div key={character.name} style={{ flex: `0 1 calc((100% - ${(itemsPerPage - 1) * 70}px - 1px) / ${itemsPerPage})`, minWidth: 0 }}>
            <CharacterTile
              character={character}
              setIsOverlayVisible={setIsOverlayVisible}
              onSaveCharacter={onSaveCharacter}
              onRegenerateCharacterFull={onRegenerateCharacterFull}
              characterIsRegenerating={characterIsRegenerating}
              imageModels={imageModels}
              projectId={projectId}
              onValidateContent={onValidateContent}
            />
          </div>
        ))}
      </div>
      <button className="next" onClick={goToNext} disabled={currentIndex >= characters.length - itemsPerPage}>❯</button>
    </div>
  );
};

export default React.memo(CharacterSlideshow);